import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import form from 'style/form.module.css';
import { postApi } from './fetchApi';
import { useApi } from './useApi';

export function Form({
  children,
  postUrl,
  successTitle,
  successDesc,
  onSuccess,
  submitButtonText,
}: {
  children: React.ReactNode;
  postUrl: string;
  successTitle: string;
  successDesc?: string;
  onSuccess?: (response: any) => void;
  submitButtonText: string;
}): JSX.Element {
  const methods = useForm();
  const toast = useToast();
  const { isSubmitting } = methods.formState;
  const serverError = methods.formState.errors.serverError as
    | { message?: string }
    | undefined;

  async function onSubmit(formValues: any): Promise<void> {
    const resp = await postApi(postUrl, formValues);

    if (resp.ok) {
      methods.reset();
      toast({
        title: successTitle,
        description: successDesc,
        status: 'success',
        position: 'top',
        isClosable: true,
      });
      if (onSuccess) {
        const contentType = resp.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          onSuccess(formValues);
        } else {
          onSuccess(await resp.json());
        }
      }
    } else {
      let serverErrorMsg = 'Server error: see log for more info';

      if (resp.headers.get('content-type')?.includes('application/json')) {
        const serverError = await resp.json();
        serverErrorMsg = serverError.message;
      } else {
        const msg = await resp.text();

        if (msg && msg.length < 100) {
          serverErrorMsg = msg;
        }
      }

      methods.setError('serverError', {
        type: 'manual',
        message: serverErrorMsg,
      });
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        onChange={(): void => methods.clearErrors('serverError')}>
        {children}
        <FormControl isInvalid={!!serverError}>
          <FormErrorMessage>{serverError?.message}</FormErrorMessage>
        </FormControl>
        <Box display="flex" justifyContent="flex-end">
          <Button
            mt={4}
            variant="primary"
            isLoading={isSubmitting}
            type="submit">
            {submitButtonText}
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
}

export function FormInput({
  label,
  name,
  register,
  isRequired,
  placeholder,
  value,
}: {
  label: string;
  name: string;
  register?: any;
  isRequired?: boolean;
  placeholder?: string;
  value?: string;
}): JSX.Element {
  const formContext = useFormContext();
  const fieldError = formContext.formState.errors[name] as
    | { message?: string }
    | undefined;

  return (
    <Box mb={4}>
      <FormControl isRequired={isRequired} isInvalid={!!fieldError}>
        <FormLabel className={form.label} htmlFor={name}>
          {label}
        </FormLabel>
        <Input
          className={form.input}
          autoComplete="off"
          placeholder={placeholder}
          defaultValue={value}
          {...(register
            ? formContext.register(name, register)
            : formContext.register(name))}
        />
        <FormErrorMessage>{fieldError?.message}</FormErrorMessage>
      </FormControl>
    </Box>
  );
}

export function FormInputNumber({
  label,
  name,
  min = 1,
  step = 1,
  register,
  isRequired,
  value = 0,
  max,
  isDisabled,
}: {
  label: string;
  name: string;
  min?: number;
  max?: number;
  step?: number;
  register?: any;
  isRequired?: boolean;
  isDisabled?: boolean;
  value: number;
}): JSX.Element {
  const formContext = useFormContext();
  const fieldError = formContext.formState.errors[name] as
    | { message?: string }
    | undefined;

  // 👇 Watch the checkbox value from the same form
  const useManualPriceFactor = useWatch({
    control: formContext.control,
    name: 'useManualPriceFactor',
  });

  // 👇 Disable field dynamically if checkbox is false
  const disabled =
    typeof useManualPriceFactor === 'boolean'
      ? !useManualPriceFactor
      : isDisabled;

  return (
    <Box mb={4}>
      <FormControl isRequired={isRequired} isInvalid={!!fieldError}>
        <FormLabel htmlFor={name}>{label}</FormLabel>
        <Input
          autoComplete="off"
          type="number"
          min={min}
          max={max}
          defaultValue={value}
          step={step}
          {...(register
            ? formContext.register(name, register)
            : formContext.register(name))}
          readOnly={disabled}
        />
        <FormErrorMessage>{fieldError?.message}</FormErrorMessage>
      </FormControl>
    </Box>
  );
}

export function FormHidden({
  name,
  data,
  register,
}: {
  name: string;
  data: any;
  register?: any;
}): JSX.Element {
  const formContext = useFormContext();
  const fieldError = formContext.formState.errors[name] as
    | { message?: string }
    | undefined;
  return (
    <FormControl isRequired={true} isInvalid={!!fieldError}>
      <Input
        autoComplete="off"
        value={data}
        type="hidden"
        {...(register
          ? formContext.register(name, register)
          : formContext.register(name))}
      />
    </FormControl>
  );
}

export function FormCustomMultiInput({
  name,
  label,
  placeholder,
  items,
  isRequired,
}: {
  name: string;
  label: string;
  placeholder: string;
  items: string[];
  isRequired: boolean;
}): JSX.Element {
  const formContext = useFormContext();
  const { setValue } = formContext;

  //const [json, setJson] = useState<string>(JSON.stringify(items));
  const [inputArray, setInputArray] = useState<string[]>(items);
  useEffect(() => {
    setValue(name, JSON.stringify(inputArray));
  }, [inputArray, setValue, name]);
  return (
    <Box mb={4}>
      <FormControl isRequired={isRequired}>
        <FormLabel htmlFor={name}>{label}</FormLabel>
        <Input
          autoComplete="off"
          value={JSON.stringify(inputArray)}
          type="hidden"
          {...formContext.register(name)}
        />
        {inputArray.map((item: string, i: number) => {
          return (
            <Input
              key={i}
              autoComplete="off"
              placeholder={placeholder}
              mb={2}
              type="text"
              value={inputArray[i]}
              onChange={(e) => {
                const ia = [...inputArray];
                if (ia[i] !== null) {
                  ia[i] = e.target.value;
                }
                setInputArray(ia);
              }}
            />
          );
        })}
        <Button
          marginRight={2}
          variant="primary"
          onClick={() => {
            const ia = [...inputArray];
            if (ia.length <= 1) return;
            ia.pop();
            setInputArray(ia);
          }}>
          -
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            const ia = [...inputArray];
            ia.push('');
            setInputArray(ia);
          }}>
          +
        </Button>
      </FormControl>
    </Box>
  );
}

export function FormCustomDropdown({
  name,
  label,
  placeholder,
  items,
  isRequired,
  selected,
}: {
  name: string;
  label: string;
  placeholder: string;
  items: Array<any>;
  isRequired: boolean;
  selected?: string;
}): JSX.Element {
  const formContext = useFormContext();
  const [value, setValue] = useState<string>(selected ?? '0');

  return (
    <Box mb={4}>
      <FormControl isRequired={isRequired}>
        <FormLabel htmlFor={name}>{label}</FormLabel>

        <Select
          placeholder={placeholder}
          {...formContext.register(name)}
          value={value}
          onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
            setValue(e.currentTarget.value);
          }}>
          {items.map((x: any) => (
            <option key={x.id} value={x.id}>
              {x.label}
            </option>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export function ArraySelect({
  name,
  label,
  items,
  isRequired,
  selected,
}: {
  name: string;
  label: string;
  items: string[];
  placeholder: string;
  selected?: number;
  isRequired: boolean;
}): JSX.Element {
  const formContext = useFormContext();
  const [value, setValue] = useState<number>(selected ?? 0);

  return (
    <Box mb={4}>
      <FormControl isRequired={isRequired}>
        <FormLabel htmlFor={name}>{label}</FormLabel>
        {items && (
          <Select
            {...formContext.register(name)}
            value={value}
            onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
              setValue(parseInt(e.currentTarget.value));
            }}>
            {items.map((item: string, i: number) => {
              return (
                <option key={i} value={i}>
                  {item}
                </option>
              );
            })}
          </Select>
        )}
      </FormControl>
    </Box>
  );
}

export function FormSelect({
  name,
  label,
  placeholder,
  itemsUrl,
  isRequired,
  selected,
  register,
}: {
  name: string;
  label: string;
  itemsUrl: string;
  isRequired: boolean;
  placeholder: string;
  selected?: string | number;
  register?: any;
}): JSX.Element {
  const itemsReq = useApi<[]>(itemsUrl);
  const formContext = useFormContext();
  const selectedValue = selected !== undefined ? selected.toString() : '';
  const [value, setValue] = useState<string>(selectedValue);

  useEffect(() => {
    if (!selectedValue) return;
    if (itemsReq.state !== 'success') return;

    setValue(selectedValue);
    if (register?.valueAsNumber) {
      formContext.setValue(name, Number(selectedValue));
    } else {
      formContext.setValue(name, selectedValue);
    }
  }, [selectedValue, itemsReq.state, name, register?.valueAsNumber]);

  const fieldRegister = register
    ? formContext.register(name, register)
    : formContext.register(name);

  return (
    <Box mb={4}>
      <FormControl isRequired={isRequired}>
        <FormLabel htmlFor={name}>{label}</FormLabel>
        {itemsReq.state !== 'error' && (
          <Select
            isDisabled={itemsReq.state !== 'success'}
            placeholder={
              itemsReq.state === 'loading' ? 'Laddar...' : placeholder
            }
            {...fieldRegister}
            value={value}
            onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
              setValue(e.currentTarget.value);
              fieldRegister.onChange(e);
            }}>
            {itemsReq.data?.map((x: any) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </Select>
        )}
        {itemsReq.state === 'error' && <p>error</p>}
      </FormControl>
    </Box>
  );
}

export function FormTextarea({
  label,
  name,
  register,
  isRequired,
  placeholder,
  value,
}: {
  label: string;
  name: string;
  register?: any;
  isRequired?: boolean;
  placeholder?: string;
  value?: string;
}): JSX.Element {
  const formContext = useFormContext();
  const fieldError = formContext.formState.errors[name] as
    | { message?: string }
    | undefined;

  return (
    <Box mb={4}>
      <FormControl isRequired={isRequired} isInvalid={!!fieldError}>
        <FormLabel className={form.label} htmlFor={name}>
          {label}
        </FormLabel>
        <Textarea
          className={form.input}
          autoComplete="off"
          placeholder={placeholder}
          defaultValue={value}
          {...(register
            ? formContext.register(name, register)
            : formContext.register(name))}
        />
        <FormErrorMessage>{fieldError?.message}</FormErrorMessage>
      </FormControl>
    </Box>
  );
}

// export function FormCheckbox({
//   label,
//   name,
//   selected,
//   isRequired,
//   register
// }: {
//   label: string;
//   name: string;
//   register?: null;
//   isRequired?: boolean;
//   placeholder?: string;
//   selected?: boolean;
// }): JSX.Element {
//   const formContext = useFormContext();

//   const [value, setValue] = useState<boolean>(selected ?? false);
//   console.log("Checkbox value", value);

//   return (
//        <Box mb={4}>
//       <FormControl
//         isRequired={isRequired}
//         isInvalid={!!formContext.formState.errors[name]}
//       >
//         <FormLabel htmlFor={name}>{label}</FormLabel>
//         <Checkbox
//         ref={register ? formContext.register(register) : formContext.register}
//         name={name}
//           ml={10}
//           colorScheme="orange"
//           checked={value}
//           onChange={(e) => {
//             setValue(e.target.checked);
//           }}
//         >
//           {label}
//         </Checkbox>
//         <FormErrorMessage>
//           {formContext.formState.errors[name]?.message as string}
//         </FormErrorMessage>
//       </FormControl>
//     </Box>
//   );
// }

export function FormCheckbox({
  label,
  name,
  value,
  isRequired,
  register,
}: {
  label: string;
  name: string;
  register?: any;
  isRequired?: boolean;
  placeholder?: string;
  value?: boolean;
}): JSX.Element {
  const formContext = useFormContext();

  // const [value, setValue] = useState<boolean>(selected ?? false);
  console.log('Checkbox value', value);

  return (
    <Box mb={4}>
      <FormControl
        isRequired={isRequired}
        isInvalid={!!formContext.formState.errors[name]}>
        <FormLabel htmlFor={name}>{label}</FormLabel>
        <Checkbox
          {...(register
            ? formContext.register(name, register)
            : formContext.register(name))}
          colorScheme="orange"
          defaultChecked={value}></Checkbox>
        <FormErrorMessage>
          {formContext.formState.errors[name]?.message as string}
        </FormErrorMessage>
      </FormControl>
    </Box>
  );
}
